import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  ExternalLink,
  RefreshCw,
  Check,
  AlertCircle,
  Settings,
  ArrowLeft,
  TrendingUp,
  History,
  Link2,
  Link2Off,
  HelpCircle,
  Receipt,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { paymentsApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort } from "@/utils/dateFormat";

// Format currency
const formatCurrency = (cents, currency = "AUD") =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
  }).format(cents / 100);

// Loading skeleton
const PaymentSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-14 w-14 rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
    </div>
    <Skeleton className="h-40 w-full" />
    <div className="grid grid-cols-3 gap-4">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
  </div>
);

export const PaymentSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [searchParams] = useSearchParams();
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Check for setup status from URL
  useEffect(() => {
    const setupStatus = searchParams.get("setup");
    if (setupStatus === "complete") {
      toast.success(t("payments.setupComplete"));
      queryClient.invalidateQueries(["payment-settings"]);
    } else if (setupStatus === "refresh") {
      toast(t("payments.setupRefresh"));
    }
  }, [queryClient, searchParams, t]);

  // Fetch payment settings and Stripe account status
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ["payment-settings"],
    queryFn: () => paymentsApi.getSettings().then((res) => res.data?.data),
  });

  // Fetch payment stats
  const { data: stats } = useQuery({
    queryKey: ["payment-stats"],
    queryFn: () => paymentsApi.getStats().then((res) => res.data?.data),
    enabled: !!settingsData?.stripeAccount?.chargesEnabled,
  });

  // Fetch payment history
  const { data: history } = useQuery({
    queryKey: ["payment-history"],
    queryFn: () =>
      paymentsApi.getHistory({ limit: 10 }).then((res) => res.data?.data || []),
    enabled:
      !!settingsData?.stripeAccount?.chargesEnabled && activeTab === "history",
  });

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: () => paymentsApi.createOnboardingLink(),
    onSuccess: (res) => {
      window.location.href = res.data?.data?.url;
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => paymentsApi.disconnect(),
    onSuccess: () => {
      toast.success(t("payments.disconnected"));
      queryClient.invalidateQueries(["payment-settings"]);
      setShowDisconnectDialog(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (updates) => paymentsApi.updateSettings(updates),
    onSuccess: () => {
      toast.success(t("payments.settingsUpdated"));
      queryClient.invalidateQueries(["payment-settings"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  // Dashboard link mutation
  const dashboardMutation = useMutation({
    mutationFn: () => paymentsApi.getDashboardLink(),
    onSuccess: (res) => {
      window.open(res.data?.data?.url, "_blank");
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || t("errors.generic"));
    },
  });

  const renderVariant = (transaction) => {
    if (transaction?.status === "succeeded") {
      return "outline";
    }
    return transaction?.status === "refunded" ? "secondary" : "destructive";
  };

  if (isLoading) {
    return <PaymentSkeleton />;
  }

  const settings = settingsData?.settings;
  const stripeAccount = settingsData?.stripeAccount;
  const isConnected = stripeAccount?.chargesEnabled;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/integrations"
            className="flex items-center justify-center h-8 w-8 rounded-lg border hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-heading tracking-tight">
              {t("payments.title")}
            </h1>
            <p className="text-muted-foreground">{t("payments.subtitle")}</p>
          </div>
        </div>
        {isConnected && (
          <Button
            variant="outline"
            onClick={() => dashboardMutation.mutate()}
            disabled={dashboardMutation.isPending}
          >
            {dashboardMutation.isPending ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="mr-2 h-4 w-4" />
            )}
            {t("payments.stripeDashboard")}
          </Button>
        )}
      </div>

      {/* Not connected state */}
      {!isConnected && (
        <>
          <Card className="bg-linear-to-r from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-2">
                {t("payments.connectStripe")}
              </h2>
              <p className="text-purple-100 mb-4">
                {t("payments.connectDesc")}
              </p>
              <Button
                variant="secondary"
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
              >
                {connectMutation.isPending ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="mr-2 h-4 w-4" />
                )}
                {t("payments.connectNow")}
              </Button>
            </CardContent>
          </Card>

          {/* Pending setup state */}
          {stripeAccount?.connected && !stripeAccount?.chargesEnabled && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("payments.setupIncomplete")}</AlertTitle>
              <AlertDescription>
                {t("payments.setupIncompleteDesc")}
                <Button
                  variant="link"
                  className="p-0 h-auto ml-2"
                  onClick={() => connectMutation.mutate()}
                >
                  {t("payments.completeSetup")} →
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Features list */}
          <Card>
            <CardHeader>
              <CardTitle>{t("payments.features")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                    <CreditCard className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">
                      {t("payments.featureCards")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Visa, Mastercard, Amex, and more
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                    <Settings className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">
                      {t("payments.featureFlexible")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Collect before, during, or after signing
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                    <Receipt className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">
                      {t("payments.featureReceipts")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Email receipts sent automatically
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                    <TrendingUp className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">
                      {t("payments.featureReporting")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Track all payments in one place
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Connected state */}
      {isConnected && (
        <>
          {/* Account status */}
          <Alert className="bg-emerald-50 border-emerald-200">
            <Check className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-emerald-800">
              {t("payments.connected")}
            </AlertTitle>
            <AlertDescription className="text-emerald-700">
              {stripeAccount?.email && `Connected as ${stripeAccount.email}. `}
              {t("payments.connectedDesc")}
            </AlertDescription>
          </Alert>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">
                    {t("payments.totalCollected")}
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(stats.total_collected_cents || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">
                    {t("payments.last30Days")}
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(stats.collected_30d_cents || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">
                    {t("payments.successfulPayments")}
                  </p>
                  <p className="text-2xl font-bold">
                    {stats.total_successful || 0}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">
                    {t("payments.refunded")}
                  </p>
                  <p className="text-2xl font-bold text-muted-foreground">
                    {formatCurrency(stats.total_refunded_cents || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">
                {t("payments.tabOverview")}
              </TabsTrigger>
              <TabsTrigger value="settings">
                {t("payments.tabSettings")}
              </TabsTrigger>
              <TabsTrigger value="history">
                {t("payments.tabHistory")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              {/* How it works */}
              <Card>
                <CardHeader>
                  <CardTitle>{t("payments.howItWorks")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                        1
                      </div>
                      <div>
                        <h4 className="font-medium">
                          {t("payments.step1Title")}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {t("payments.step1Desc")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                        2
                      </div>
                      <div>
                        <h4 className="font-medium">
                          {t("payments.step2Title")}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {t("payments.step2Desc")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                        3
                      </div>
                      <div>
                        <h4 className="font-medium">
                          {t("payments.step3Title")}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {t("payments.step3Desc")}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t("payments.paymentSettings")}</CardTitle>
                  <CardDescription>
                    {t("payments.paymentSettingsDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>{t("payments.defaultCurrency")}</Label>
                      <Select
                        value={settings?.defaultCurrency || "USD"}
                        onValueChange={(value) =>
                          updateSettingsMutation.mutate({
                            defaultCurrency: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">
                            {t("currencies.usd")}
                          </SelectItem>
                          <SelectItem value="EUR">
                            {t("currencies.eur")}
                          </SelectItem>
                          <SelectItem value="GBP">
                            GBP - British Pound
                          </SelectItem>
                          <SelectItem value="CAD">
                            CAD - Canadian Dollar
                          </SelectItem>
                          <SelectItem value="AUD">
                            AUD - Australian Dollar
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t("payments.paymentTiming")}</Label>
                      <Select
                        value={settings?.paymentTiming || "before_signing"}
                        onValueChange={(value) =>
                          updateSettingsMutation.mutate({
                            paymentTiming: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="before_signing">
                            Before signing
                          </SelectItem>
                          <SelectItem value="after_signing">
                            After signing
                          </SelectItem>
                          <SelectItem value="after_completion">
                            After all sign
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="statementDescriptor">
                      {t("payments.statementDescriptor")}
                    </Label>
                    <Input
                      id="statementDescriptor"
                      placeholder={t("placeholders.appName")}
                      maxLength={22}
                      defaultValue={settings?.statementDescriptor || ""}
                      onBlur={(e) => {
                        if (e.target.value !== settings?.statementDescriptor) {
                          updateSettingsMutation.mutate({
                            statementDescriptor: e.target.value,
                          });
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("payments.statementDescriptorHelp")}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("payments.passFees")}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t("payments.passFeesHelp")}
                      </p>
                    </div>
                    <Switch
                      checked={settings?.passFeeToCustomer || false}
                      onCheckedChange={(checked) =>
                        updateSettingsMutation.mutate({
                          passFeeToCustomer: checked,
                        })
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Disconnect */}
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-destructive">
                    {t("payments.dangerZone")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {t("payments.disconnectStripe")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("payments.disconnectHelp")}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setShowDisconnectDialog(true)}
                    >
                      <Link2Off className="mr-2 h-4 w-4" />
                      {t("payments.disconnect")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t("payments.recentPayments")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {history?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>{t("payments.noPayments")}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("payments.document")}</TableHead>
                          <TableHead>{t("payments.amount")}</TableHead>
                          <TableHead>{t("payments.status")}</TableHead>
                          <TableHead>{t("payments.payer")}</TableHead>
                          <TableHead>{t("payments.date")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history?.map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell className="font-medium">
                              {transaction.envelope_name ||
                                t("common.document")}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(
                                transaction.amount_cents,
                                transaction.currency,
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={renderVariant(transaction)}
                                className={
                                  transaction.status === "succeeded"
                                    ? "bg-emerald-500/10 text-emerald-600 border-emerald-200"
                                    : ""
                                }
                              >
                                {transaction.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {transaction.payer_email || "-"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDateShort(transaction.created_at, user)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Help Section */}
      <Card className="bg-muted/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <HelpCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">{t("payments.needHelp")}</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  <a
                    href="/docs/payments"
                    className="text-primary hover:underline"
                  >
                    {t("payments.paymentGuide")} →
                  </a>
                </li>
                <li>
                  <a
                    href="https://stripe.com/docs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {t("payments.stripeDocs")} →
                  </a>
                </li>
                <li>
                  <a href="/support" className="text-primary hover:underline">
                    {t("payments.contactSupport")} →
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Disconnect Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("payments.disconnectTitle")}</DialogTitle>
            <DialogDescription>
              {t("payments.disconnectWarning")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisconnectDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
            >
              {disconnectMutation.isPending
                ? t("common.disconnecting")
                : t("payments.disconnect")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentSettings;
